/**
 * ReceiptPage · v2.7.65+E145 (2026-02-03)
 * ═════════════════════════════════════════════════════════════════════
 * Public trip receipt permalink. Prints cleanly (single column, black
 * ink, no chrome) and offers an SMS-send hook for the driver.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Printer, MessageSquare, CheckCircle2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ReceiptPage() {
  const { tripId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone]     = useState("");
  const [sending, setSending] = useState(false);
  const [sentOk,  setSentOk]  = useState(false);
  const [driverToken, setDriverToken] = useState("");

  useEffect(() => {
    setDriverToken(localStorage.getItem("rtg_token") || "");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/trips/${tripId}/receipt`);
        setReceipt(data);
        setPhone(data.customer_phone || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Receipt not found");
      } finally { setLoading(false); }
    })();
  }, [tripId]);

  const sendSms = useCallback(async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/trips/${tripId}/receipt/sms`,
        { to_phone: phone.trim() },
        { headers: driverToken ? { Authorization: `Bearer ${driverToken}` } : {} },
      );
      if (data.ok) {
        toast.success("Receipt SMS sent");
        setSentOk(true);
      } else if (data.error === "twilio_not_configured") {
        toast.info("Preview mode — SMS logged to outbox (Twilio DRY-RUN)");
        setSentOk(true);
      } else {
        toast.error(data.error || "SMS failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`SMS failed: ${detail}`);
    } finally { setSending(false); }
  }, [phone, tripId, driverToken]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-slate-900">
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin"/>Loading receipt…</div>
      </div>
    );
  }
  if (!receipt) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-slate-900">
        <div className="text-slate-500">Receipt not found.</div>
      </div>
    );
  }

  const created = receipt.created_at ? new Date(receipt.created_at) : null;

  return (
    <div className="min-h-screen bg-white text-slate-900 py-6 px-4 print:py-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>

      <div className="max-w-md mx-auto space-y-4">
        {/* Print controls (hidden on printout) */}
        <div className="no-print flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Trip Receipt</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="receipt-print"
              className="h-9 px-3 rounded border border-slate-300 text-sm font-bold hover:bg-slate-100 flex items-center gap-1.5"
            >
              <Printer className="h-4 w-4"/>Print
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="border-b-2 border-black pb-2">
          <div className="text-lg font-black text-black uppercase tracking-wide">Buller Taxi Services, LLC</div>
          <div className="text-xs text-slate-600">Weatherford, OK · Dispatch Receipt</div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Trip ID</div>
            <div className="font-mono text-[11px] break-all" data-testid="receipt-trip-id">{receipt.trip_id}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Date</div>
            <div className="font-mono text-[11px]">{created ? created.toLocaleString() : "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Rider</div>
            <div className="font-bold" data-testid="receipt-rider">{receipt.customer_name || "—"}</div>
            {receipt.customer_phone && <div className="text-slate-600 text-[11px]">{receipt.customer_phone}</div>}
          </div>
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Trip</div>
            <div className="text-sm">
              {receipt.origin || "—"}
              <span className="mx-2 text-slate-400">→</span>
              {receipt.destination || receipt.dropoff_label || "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {receipt.passengers} passenger{receipt.passengers === 1 ? "" : "s"}
              {receipt.round_trip ? " · Round trip" : ""}
              {receipt.stops > 0 ? ` · ${receipt.stops} extra stop${receipt.stops === 1 ? "" : "s"}` : ""}
              {receipt.miles > 0 ? ` · ${receipt.miles} mi` : ""}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="border-y border-slate-300 py-2 space-y-1" data-testid="receipt-line-items">
          {(receipt.line_items || []).length === 0 ? (
            <div className="flex justify-between text-sm">
              <span>Base fare</span>
              <span className="font-mono font-bold">{fmt(receipt.subtotal)}</span>
            </div>
          ) : receipt.line_items.map((li, i) => (
            <div key={i} className="flex justify-between text-xs" data-testid={`receipt-line-${i}`}>
              <span className="text-slate-600 mr-2 truncate">{li.label}</span>
              <span className={`font-mono font-bold ${li.amount < 0 ? "text-emerald-700" : ""}`}>
                {li.amount < 0 ? "-" : ""}{fmt(Math.abs(li.amount))}
              </span>
            </div>
          ))}
          {receipt.formula_note && (
            <div className="text-[9px] italic text-slate-500 pt-1">{receipt.formula_note}</div>
          )}
        </div>

        {/* Totals */}
        <div className="text-xl font-black text-black flex justify-between border-b-4 border-black pb-2">
          <span>TOTAL</span>
          <span data-testid="receipt-total">{fmt(receipt.total)}</span>
        </div>
        {receipt.payment_method && (
          <div className="text-[11px] text-slate-500">Payment: {receipt.payment_method}</div>
        )}

        {/* v2.7.65+E146 · Phase 3.5 · Print-friendly QR of the receipt
            permalink. Rider scans off the printout — no SMS needed. */}
        <div className="flex items-center gap-3 pt-2" data-testid="receipt-qr-block">
          <QRCodeSVG
            value={typeof window !== "undefined" ? window.location.href : `https://bullertaxi.com/receipt/${receipt.trip_id}`}
            size={72}
            level="M"
            includeMargin={false}
            data-testid="receipt-qr-svg"
          />
          <div className="text-[10px] text-slate-500 leading-tight">
            <div className="font-bold uppercase tracking-widest text-slate-700">Scan to open</div>
            <div>Point your camera at the code above to reopen this receipt on your phone anytime.</div>
          </div>
        </div>

        {/* SMS block (hidden on printout) */}
        <div className="no-print pt-2 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Text receipt link to</div>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(405) 555-0199"
              data-testid="receipt-sms-phone"
              className="flex-1 h-10 px-3 rounded border border-slate-300 text-sm"
            />
            <button
              onClick={sendSms}
              disabled={sending || !driverToken}
              data-testid="receipt-sms-btn"
              title={!driverToken ? "Driver sign-in required to text a receipt" : "Send SMS"}
              className="h-10 px-4 rounded bg-black text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin"/>
               : sentOk ? <CheckCircle2 className="h-4 w-4"/>
               : <MessageSquare className="h-4 w-4"/>}
              {sentOk ? "Sent" : "SMS"}
            </button>
          </div>
          {!driverToken && (
            <div className="text-[10px] italic text-slate-400">
              Sign in as a driver to enable SMS. Passengers can still print or share this link.
            </div>
          )}
        </div>

        <div className="text-center text-[10px] text-slate-400 pt-2">
          Thanks for riding with us · bullertaxi.com
        </div>
      </div>
    </div>
  );
}
